'use client';

import type {
  BucketInference,
  ImpactBundle,
  QualitativeImpactBundle,
  Storyline,
} from '@akem/shared';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import DeltaChip from './DeltaChip.js';

type Variant = 'widget' | 'profile';

interface StorylineDetailsProps {
  storyline: Storyline;
  variant?: Variant;
}

const SurfaceCard: React.FC<{
  title: string;
  subtitle?: string;
  adornment?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, subtitle, adornment, children }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 3,
      p: { xs: 2, md: 2.5 },
      backdropFilter: 'blur(8px)',
      borderColor: 'rgba(148,163,184,0.28)',
      bgcolor: (theme) =>
        theme.palette.mode === 'dark'
          ? 'rgba(15,23,42,0.68)'
          : 'rgba(255,255,255,0.92)',
    }}
  >
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: '0.65rem',
              color: 'text.secondary',
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {adornment}
      </Stack>
      {children}
    </Stack>
  </Paper>
);

const HeroStat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack spacing={0.4}>
    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: 'common.white', fontWeight: 600 }}>
      {value}
    </Typography>
  </Stack>
);

const BUCKET_LABELS: Record<string, string> = {
  regulatory: 'Regulatory',
  politics: 'Politics',
  customers: 'Customers',
  mna: 'M&A',
  logistics: 'Logistics',
  marketTrends: 'Market Trends',
  suppliers: 'Suppliers',
  themes: 'Themes',
  rawMaterials: 'Raw Materials',
};

const formatBucketLabel = (bucket?: string, fallback?: string): string => {
  if (!bucket) return fallback || 'Bucket';
  return BUCKET_LABELS[bucket] || fallback || bucket.replace(/([A-Z])/g, ' $1').replace(/^[a-z]/, (c) => c.toUpperCase());
};

const formatGeneratedChip = (iso?: string): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `Generated ${date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

function asQual(
  bundle: ImpactBundle | QualitativeImpactBundle | undefined | null
): bundle is QualitativeImpactBundle {
  return !!bundle && (bundle as any).theme != null && (bundle as any).direction != null && (bundle as any).strength != null;
}

function extractPrimaryImpact(
  story: Storyline
): { type: 'qual' | 'num'; qual?: QualitativeImpactBundle; num?: ImpactBundle } | null {
  const first = Array.isArray(story.impacts) && story.impacts.length ? (story.impacts[0] as any) : null;
  if (!first) return null;
  if (asQual(first)) return { type: 'qual', qual: first };
  return { type: 'num', num: first as ImpactBundle };
}

function metricLabel(metric: string): string {
  const m = (metric || '').toLowerCase();
  if (m.includes('margin')) return 'Gross margin';
  if (m.includes('eps')) return 'EPS';
  if (m.includes('revenue')) return 'Revenue growth';
  return 'Second-order';
}

function formatLikelyOutcome(story: Storyline): string {
  const pi = extractPrimaryImpact(story);
  if (!pi) return 'No explicit outcome provided.';
  if (pi.type === 'num' && pi.num) {
    const { magnitude, units, lag, affected_metric } = pi.num;
    const signed = units === 'bps'
      ? `${magnitude >= 0 ? '+' : ''}${Math.round(magnitude)} bps`
      : `${magnitude >= 0 ? '+' : ''}${(Math.round((magnitude as number) * 100) / 100).toFixed(2)}%`;
    return `${metricLabel(String(affected_metric))} ${signed} (${lag})`;
  }
  if (pi.type === 'qual' && pi.qual) {
    const { direction, strength, lag } = pi.qual;
    return `${direction[0].toUpperCase()}${direction.slice(1)} (${strength}), expected ${lag}`;
  }
  return 'No explicit outcome provided.';
}

function uniqueBy<T>(arr: T[], key: (x: T) => string): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of arr) {
    const k = key(item);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(item);
    }
  }
  return out;
}

export const StorylineDetails: React.FC<StorylineDetailsProps> = ({ storyline }) => {
  const [copiedId, setCopiedId] = useState(false);
  const primary = extractPrimaryImpact(storyline);
  const bucketInference: BucketInference | undefined = storyline.bucketInference ?? undefined;

  const deepFields = (storyline as any) || {};
  const impactOrder: string | undefined = deepFields.orderOfImpact || deepFields.impact_order;
  const pathOfImpacts: string[] | undefined = Array.isArray(deepFields.pathOfImpacts)
    ? deepFields.pathOfImpacts
    : Array.isArray(deepFields.path_of_impacts)
    ? deepFields.path_of_impacts
    : undefined;
  const exposures: string[] | undefined = Array.isArray(deepFields.exposures)
    ? deepFields.exposures
    : undefined;
  const bridgeEntities: string[] | undefined = Array.isArray(deepFields.bridgeEntities)
    ? deepFields.bridgeEntities
    : undefined;
  const importanceScore: number | undefined =
    typeof deepFields.importanceScore === 'number' ? deepFields.importanceScore : undefined;
  const thesis: string | undefined = typeof deepFields.thesis === 'string' ? deepFields.thesis : undefined;
  const researchNotes: string | undefined =
    typeof deepFields.researchNotes === 'string' ? deepFields.researchNotes : undefined;
  const timeHorizon: string | undefined = deepFields.horizon || deepFields.time_horizon;
  const impactDirection: string | undefined = deepFields.impactDirection || deepFields.impact_direction;
  const monitorQuery: string | undefined = deepFields.monitorQuery || deepFields.monitor_query;
  const monitorId: string | undefined = deepFields.monitorId || deepFields.monitor_id;
  const monitorSpec: { query?: string; cadence?: string } | undefined =
    deepFields.monitorSpec || deepFields.monitor_spec;

  const provenance =
    (primary?.type === 'qual' ? (primary.qual?.provenance as any[]) : (primary?.num as any)?.provenance) || [];
  const provSources: Array<{ label: string; url?: string | null }> = provenance
    .map((p: any) => ({ label: p?.title || p?.url || p?.artifactId || 'source', url: p?.url }))
    .filter((p: any) => typeof p.label === 'string' && p.label.length > 0);

  const citations = (storyline.claims || []).flatMap((c) => (c as any).citations || []);
  const citationSources: Array<{ label: string; url?: string | null }> = citations.map((ct: any) => ({
    label: `${ct?.type || 'source'}: ${ct?.ref || ''}`,
    url: ct?.ref?.startsWith('http') ? ct.ref : undefined,
  }));

  const sources = uniqueBy([...provSources, ...citationSources], (s) => `${s.label}|${s.url ?? ''}`);

  const confidenceLabel =
    typeof storyline.confidence === 'number'
      ? `${Math.round(Math.max(0, Math.min(1, storyline.confidence)) * 100)}%`
      : 'Not rated';
  const formattedAsOf = storyline.asOf
    ? new Date(storyline.asOf).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unknown';
  const bucketLabel = formatBucketLabel(
    bucketInference?.bucket,
    storyline.bucketInference?.source || storyline.trigger_event?.tier
  );

  const anchorEdges = uniqueBy(
    (storyline.claims || []).flatMap((claim: any) => claim.anchors || []),
    (anchor: any) => anchor?.edge || ''
  )
    .filter((anchor) => Boolean(anchor?.edge))
    .slice(0, 6);
  const displayClaims = (storyline.claims || []).slice(0, 3);
  const whatWouldChange = Array.isArray(storyline.what_would_change)
    ? storyline.what_would_change.slice(0, 6)
    : [];
  const historicalEvidence: Array<{
    example: string;
    period: string | null;
    description: string;
    relevance: string;
    url?: string;
    source?: string;
    matchedExposures?: string[];
    score?: number;
    kind?: string;
  }> = Array.isArray((storyline as any).historicalEvidence)
    ? (storyline as any).historicalEvidence
    : [];
  const eventWindow = (storyline as any)?.analytics?.eventWindow;
  const storylineId = useMemo(() => storyline.id || 'unknown', [storyline.id]);

  const handleCopyId = async (): Promise<void> => {
    try {
      if (typeof navigator === 'undefined' || !navigator.clipboard) return;
      await navigator.clipboard.writeText(storylineId);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1600);
    } catch {
      setCopiedId(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper
        sx={{
          position: 'relative',
          borderRadius: 3,
          p: { xs: 2.5, md: 3 },
          color: 'common.white',
          overflow: 'hidden',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(12,20,39,0.95), rgba(26,31,58,0.85) 50%, rgba(51,65,85,0.9))'
              : 'linear-gradient(135deg, #0f172a, #1f2937 50%, #334155)',
          boxShadow: '0 25px 45px rgba(15,23,42,0.55)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.4,
            borderRadius: 999,
            bgcolor: 'rgba(15,23,42,0.5)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 0.6 }}>
            #{storylineId.slice(0, 8)}
          </Typography>
          <Tooltip title={copiedId ? 'Copied' : 'Copy storyline ID'}>
            <IconButton
              size="small"
              onClick={handleCopyId}
              sx={{ color: 'rgba(255,255,255,0.8)' }}
            >
              {copiedId ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)', letterSpacing: 1.1 }}>
              {bucketLabel}
            </Typography>
            {impactOrder ? (
              <Chip
                size="small"
                label={`Order: ${impactOrder}`}
                sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
              />
            ) : null}
            {typeof importanceScore === 'number' ? (
              <Chip
                size="small"
                label={`Importance ${importanceScore}`}
                sx={{ bgcolor: 'grey.800', color: 'common.white' }}
              />
            ) : null}
            {impactDirection ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Direction: ${impactDirection}`}
                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'inherit' }}
              />
            ) : null}
            {timeHorizon ? (
              <Chip
                size="small"
                variant="outlined"
                label={`Horizon: ${timeHorizon}`}
                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'inherit' }}
              />
            ) : null}
          </Stack>
        <Typography
          variant="h4"
            sx={{
              fontWeight: 600,
              lineHeight: 1.25,
              letterSpacing: '-0.02em',
            }}
          >
            {storyline.title || 'Storyline'}
          </Typography>
          {thesis ? (
            <Typography
              variant="body1"
              sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', lineHeight: 1.6 }}
            >
              {thesis}
            </Typography>
          ) : null}
          {storyline.summary ? (
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.95rem', lineHeight: 1.5 }}
            >
              {storyline.summary}
            </Typography>
          ) : null}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} flexWrap="wrap">
            <HeroStat label="Confidence" value={confidenceLabel} />
            <HeroStat label="Trigger Tier" value={(storyline.trigger_event?.tier || 'slow').toUpperCase()} />
            <HeroStat
              label="Last Refresh"
              value={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{formattedAsOf}</span>
                  {monitorId ? (
                    <Chip
                      size="small"
                      label={`Monitoring ${monitorSpec?.cadence || 'active'}`}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        color: '#34d399',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        boxShadow: '0 0 8px rgba(16, 185, 129, 0.25)',
                        '& .MuiChip-label': { px: 0.8 },
                      }}
                    />
                  ) : null}
                </Stack>
              }
            />
            <HeroStat
              label="Exposure Anchors"
              value={storyline.hops ? `${storyline.hops} exposure${storyline.hops === 1 ? '' : 's'}` : 'Instrument anchor'}
            />
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Stack spacing={2.5}>
            <SurfaceCard title="Impact Snapshot" subtitle="How the storyline lands">
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {formatLikelyOutcome(storyline)}
                </Typography>
                {eventWindow ? (
                  <>
                    <Divider />
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            AR[−1,+3]
                          </Typography>
                          {typeof eventWindow.ar_m1_p3 === 'number' ? (
                            <DeltaChip value={eventWindow.ar_m1_p3} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            AR[0,+5]
                          </Typography>
                          {typeof eventWindow.ar_0_p5 === 'number' ? (
                            <DeltaChip value={eventWindow.ar_0_p5} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Gap
                          </Typography>
                          {typeof eventWindow.gap_pct === 'number' ? (
                            <DeltaChip value={eventWindow.gap_pct} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            Volume z
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              typeof eventWindow.vol_z === 'number'
                                ? eventWindow.vol_z.toFixed(2)
                                : '—'
                            }
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </>
                ) : null}
              </Stack>
            </SurfaceCard>

            <SurfaceCard
              title="Mechanisms & Pathways"
              subtitle="How the signal propagates"
              adornment={
                primary?.type === 'qual' && primary.qual ? (
                  <Chip
                    size="small"
                    label={`${primary.qual.direction} • ${primary.qual.strength}`}
                    variant="outlined"
                  />
                ) : null
              }
            >
              <Stack spacing={1.5}>
                {Array.isArray(storyline.mechanisms) && storyline.mechanisms.length ? (
                  <Stack spacing={1}>
                    {storyline.mechanisms.slice(0, 10).map((mechanism, idx) => (
                      <Box
                        key={`mechanism-${idx}`}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'rgba(148,163,184,0.2)',
                          bgcolor: 'rgba(0,0,0,0.1)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                        }}
                      >
                         <Box
                            sx={{
                              mt: 0.75,
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              flexShrink: 0,
                              opacity: 0.8
                            }}
                          />
                        <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                          {mechanism.name}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No mechanisms were attached to this storyline.
                  </Typography>
                )}
                {anchorEdges.length || (exposures && exposures.length) ? (
                  <>
                    <Divider />
                    <Stack spacing={0.75}>
                      <Typography variant="caption" color="text.secondary">
                        Exposure keys
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {anchorEdges.map((anchor, idx) => (
                          <Chip key={`anchor-${idx}`} label={anchor.edge} size="small" variant="outlined" sx={{ borderRadius: 2 }} />
                        ))}
                        {Array.isArray(exposures)
                          ? exposures.slice(0, 6).map((exp) => (
                              <Chip
                                key={`exp-${exp}`}
                                label={exp}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                              />
                            ))
                          : null}
                        {Array.isArray(bridgeEntities)
                          ? bridgeEntities.slice(0, 6).map((ent) => (
                              <Chip
                                key={`bridge-${ent}`}
                                label={ent}
                                size="small"
                                variant="outlined"
                                sx={{ borderRadius: 2 }}
                              />
                            ))
                          : null}
                      </Stack>
                    </Stack>
                  </>
                ) : null}
              </Stack>
            </SurfaceCard>

            {whatWouldChange.length ? (
              <SurfaceCard title="What needs to move" subtitle="Signals we're watching">
                <Stack component="ol" spacing={0.8} sx={{ pl: 2, m: 0 }}>
                  {whatWouldChange.map((item, idx) => (
                    <Typography key={`wwc-${idx}`} component="li" variant="body2" color="text.secondary">
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </SurfaceCard>
            ) : null}
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={2.5}>
            <SurfaceCard title="Claims & Evidence" subtitle="Top-ranked narratives">
              <Stack spacing={2}>
                {displayClaims.length ? (
                  displayClaims.map((claim, idx) => {
                    const citation = Array.isArray(claim.citations) ? claim.citations[0] : null;
                    return (
                      <Stack
                        key={`claim-${idx}`}
                        spacing={0.75}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'rgba(148,163,184,0.35)',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Storyline {idx + 1}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {claim.text}
                        </Typography>
                        {citation ? (
                          <Typography variant="caption" color="text.secondary">
                            {citation.type}:{' '}
                            {citation.ref && citation.ref.startsWith('http') ? (
                              <Link
                                href={citation.ref}
                                target="_blank"
                                rel="noreferrer"
                                color="inherit"
                                underline="hover"
                              >
                                {citation.ref}
                              </Link>
                            ) : (
                              citation.ref
                            )}
                          </Typography>
                        ) : null}
                      </Stack>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No explicit claims captured.
                  </Typography>
                )}
              </Stack>
            </SurfaceCard>

            {historicalEvidence.length ? (
              <SurfaceCard title="Historical context" subtitle="Adjacent precedent">
                <Stack spacing={1.25}>
                  {historicalEvidence.slice(0, 6).map((row, idx) => (
                    <Stack
                      key={`history-${idx}`}
                      spacing={0.4}
                      sx={{
                        borderBottom:
                          idx < historicalEvidence.length - 1 ? '1px dashed rgba(148,163,184,0.3)' : 'none',
                        pb: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {row.example}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {row.period ? <Chip size="small" label={row.period} /> : null}
                        {row.source ? <Chip size="small" variant="outlined" label={row.source} /> : null}
                        {Array.isArray(row.matchedExposures)
                          ? row.matchedExposures.slice(0, 3).map((m) => (
                              <Chip key={m} size="small" variant="outlined" label={m} />
                            ))
                          : null}
                        {typeof row.score === 'number' ? (
                          <Chip size="small" label={`Score ${Math.round(row.score * 100) / 100}`} />
                        ) : null}
                        {row.kind ? <Chip size="small" variant="outlined" label={row.kind} /> : null}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {row.description}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {row.relevance}
                      </Typography>
                      {row.url ? (
                        <Link href={row.url} underline="hover" target="_blank" rel="noreferrer">
                          {row.url}
                        </Link>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
              </SurfaceCard>
            ) : null}

            {monitorQuery || researchNotes ? (
              <SurfaceCard title="Monitoring & Notes">
                <Stack spacing={1}>
                  {monitorQuery ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Monitor query
                      </Typography>
                      <Typography variant="body2">{monitorQuery}</Typography>
                    </Box>
                  ) : null}
                  {researchNotes ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Research notes
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {researchNotes}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </SurfaceCard>
            ) : null}

            {sources.length ? (
              <SurfaceCard title="Source ledger" subtitle="Citations + provenance">
                <Stack spacing={1}>
                  {sources.map((source, idx) => (
                    <Stack
                      key={`source-${idx}`}
                      direction="row"
                      alignItems="center"
                      spacing={1.25}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: 'background.default',
                        border: '1px solid',
                        borderColor: 'rgba(148,163,184,0.25)',
                      }}
                    >
                      <Box
                        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }}
                      />
                      {source.url ? (
                        <Link href={source.url} underline="hover" target="_blank" rel="noreferrer">
                          {source.label}
                        </Link>
                      ) : (
                        <Typography variant="body2">{source.label}</Typography>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </SurfaceCard>
            ) : null}

            {provenance.length ? (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Provenance detail</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1.5}>
                    {provenance.map((prov: any, idx: number) => (
                      <Stack key={`prov-${idx}`} spacing={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {prov.title || prov.url || prov.artifactId || 'Source'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {prov.type || 'Evidence'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {prov.summary || prov.reasoning || 'No summary available.'}
                        </Typography>
                        {prov.url ? (
                          <Link href={prov.url} target="_blank" rel="noreferrer">
                            {prov.url}
                          </Link>
                        ) : null}
                      </Stack>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ) : null}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
